import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { Labels, SdfCardLabels } from 'src/app/app.models';
import {
  overlayFadeInOutAnimation,
  slideInOutAnimation,
  dropdownAnimation,
} from '../../shared/reusable-animations/card-estimate-drawer-animations';
import { MobileBreakPointsService } from '../../services/mobileBreakPoints.service';
import { FxRateResults } from '../../store/fee-quote/types';
import { getFxRate } from '../../store/fee-quote/fee-quote.selectors';
import { SetUserTrnAmountAction } from 'src/app/store/fee-quote/fee-quote.actions';


@Component({
  selector: 'app-amount-and-exchange-rate',
  templateUrl: './amount-and-exchange-rate.component.html',
  styleUrls: ['./amount-and-exchange-rate.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AmountAndExchangeRateComponent),
      multi: true,
    },
  ],
  animations: [
    slideInOutAnimation,
    overlayFadeInOutAnimation,
    dropdownAnimation,
  ],
})
export class AmountAndExchangeRateComponent
  implements OnInit, ControlValueAccessor, OnChanges
{
  amountControl: FormControl<string | null> = new FormControl('', [
    Validators.required,
    Validators.min(1),
  ]);
  private _value: string;
  isDisabled: boolean = false;
  onChange: Function = (value: string) => {};
  onTouch: Function = () => {};
  @Input() receiveCurrencies: string[];
  @Input() isFxRateError = false;
  @Input() receiveCurrency: string | undefined;
  @Input() fxRateError!: boolean | null;
  @Input() countryName: string | undefined;
  @Input() enteredAmount: string | number;
  public _disableAmount: boolean = false;
  @Input() public set disableAmount(disAmt: boolean | undefined) {
    this._disableAmount = typeof disAmt !== 'undefined' ? disAmt : false;
  }
  @Output() emitRecieveCurrency: EventEmitter<string> = new EventEmitter();
  @Output() formatAmount: EventEmitter<boolean> = new EventEmitter();
  isMobile$: Observable<boolean>;
  isDropdownOpen: boolean = false;
  exchangeAmount: number | string;
  sdfCardLabels: SdfCardLabels;

  public _fxRateObj: FxRateResults;
  @Input() public set fxRateObj(fxRateObj: FxRateResults) {
    this._fxRateObj = fxRateObj;
  }

  public _labels: Labels;
  @Input() public set labels(labels: Labels) {
    this._labels = labels;
    this.sdfCardLabels = labels.stellar.sdfcard;
  }

  public get labels() {
    return this._labels;
  }

  constructor(
    private mobileBreakPointsService: MobileBreakPointsService,
    private rootFormGroup: FormGroupDirective,
    private store: Store<any>
  ) {}

  @ViewChild('exchangeDropdown', { static: false, read: ElementRef })
  exChangeDropdown!: ElementRef;
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (
      this.exChangeDropdown &&
      !this.exChangeDropdown.nativeElement.contains(event.target)
    ) {
      this.isDropdownOpen = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fxRateObj']?.currentValue) {
      if (!this.rootFormGroup.control.contains('currency')) {
        this.rootFormGroup.control.addControl(
          'currency',
          new FormControl('', [Validators.required, Validators.min(3)])
        );
        this.rootFormGroup.control
          .get('currency')
          ?.setValue(changes['fxRateObj']?.currentValue?.currency);
      }
    }
  }

  ngOnInit(): void {
    this.amountControl.valueChanges.subscribe(amount => {
      this.store.dispatch(SetUserTrnAmountAction({amount}))
    })
    if (
      (typeof this.amountControl.value === 'undefined' ||
        this.amountControl.value === '') &&
      this.enteredAmount
    ) {
      this.amountControl.setValue(String(this.enteredAmount));
    }

    this.store.select(getFxRate).subscribe((storeFxRate) => {
      if (storeFxRate) {
        this._fxRateObj = storeFxRate;
      }

      if (this.enteredAmount && this._fxRateObj?.fxRate) {
        const amt =
          Number(this.enteredAmount) * Number(this._fxRateObj?.fxRate);
        this.exchangeAmount = amt ? amt.toFixed(2) : 0;
      } else {
        this.exchangeAmount = 0;
      }
    });

    this.amountControl.valueChanges.subscribe((val) => {
      this._value = val!;
      this.enteredAmount = String(val);
      if (this._fxRateObj?.fxRate) {
        const amt = Number(val) * Number(this._fxRateObj?.fxRate);
        this.exchangeAmount = amt ? amt.toFixed(2) : 0;
      }
      this.onChange(val);
      this.onTouch();
    });
    this.isMobile$ = this.mobileBreakPointsService
      .mobileBreakPoints$ as Observable<boolean>;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  writeValue(obj: any): void {
    if (obj !== undefined && obj !== null) {
      this._value = obj;
    }
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
  exchangeCurrencyToggle() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  changeCurrency(rc: string) {
    this.emitRecieveCurrency.emit(rc);
    this.isDropdownOpen = false;
  }

  getRecieveCurrencyCode(receiveCurrency: string): string {
    if (this.receiveCurrencies.includes(receiveCurrency)) {
      return receiveCurrency;
    } else {
      return this.receiveCurrencies[0];
    }
  }
}
